import { APIErrorResponse, isAPIErrorResponse } from '../serviceHelpers/APIErrorResponse';
import Identity from './identity';
import { headers } from './constants';
import JSONResponseHandler from '../serviceHelpers/JSONResponseHandler';
import getFacebookLoginStatus from './getFacebookLoginStatus';
import { isFacebookLoginResponse } from './socialSignOnTypes';
import { fireEvent } from '../utils/events';

declare global {
  interface globalThis {
    Sales: any;
  }
}

/**
 * Terminates a session. This will also clear out `userIdentity`.  Additionally, this function will also attempt to
 * log out from 3rd party sign in providers (e.g. Facebook, Google).
 *
 * @returns
 * A __Promise__ which resolves to null (if successful). Clears out `userIdentity`.
 *
 * <details>
 * <summary>
 * On error (throws)
 * </summary>
 *
 * ```
 * See documentation for corresponding error codes:
 * https://redirector.arcpublishing.com/alc/docs/swagger/?url=./arc-products/arc-identity-v1.json#/Authentication
 * ```
 * </details>
 *
 */
export default async function logout(): Promise<null | APIErrorResponse> {
  if (Identity.userIdentity.accessToken) {
    return Identity.heartbeat().then(() =>
      fetch(`${Identity.apiOrigin}/identity/public/v1/auth/token`, {
        method: 'DELETE',
        headers: {
          Authorization: `Bearer ${Identity.userIdentity.accessToken}`,
          ...headers
        }
      })
        .then(JSONResponseHandler)
        .then(async json => {
          Identity.clearSession();
          dispatchLogoutEvent();
          await facebookSignOut();

          if (globalThis.gapi) {
            try {
              globalThis.gapi.auth2.getAuthInstance().signOut();
            } catch (e) {
              console.error('error signing out of google', e);
            }
          }

          if (globalThis.google) {
            try {
              globalThis.google.accounts.id.disableAutoSelect();
            } catch (e) {
              console.error('[Arc Identity SDK]: error signing out of google', e);
            }
          }

          if (isAPIErrorResponse(json)) {
            throw json;
          } else {
            return null;
          }
        })
        .catch(e => {
          return Promise.reject(e);
        })
    );
  } else {
    Identity.clearSession();
    dispatchLogoutEvent();
    if (globalThis.gapi) {
      try {
        globalThis.gapi.auth2.getAuthInstance().signOut();
      } catch (e) {
        console.error('error signing out of google', e);
      }
    }
    if (globalThis.google) {
      try {
        globalThis.google.accounts.id.disableAutoSelect();
      } catch (e) {
        console.error('[Arc Identity SDK]: error signing out of google', e);
      }
    }
    try {
      await facebookSignOut();
    } catch (e) {
      console.error('error signing out of facebook', e);
    }
    throw new Error('No access token found, user is already logged out');
  }
}

/**
 * @hidden
 * If the user is logged into Facebook, logs the user out of Facebook.
 * This function is called as part of [logout] if Facebook sign on is enabled.
 */
export const facebookSignOut = async (): Promise<null | Error> => {
  if (window.FB) {
    const facebookLoginStatus = await getFacebookLoginStatus();
    if (
      isFacebookLoginResponse(facebookLoginStatus) &&
      facebookLoginStatus.status === 'connected'
    ) {
      try {
        return window.FB.logout(() => {
          return Promise.resolve(null);
        });
      } catch (e) {
        return Promise.reject(e);
      }
    }
  }

  return Promise.resolve(null);
};

export const dispatchLogoutEvent = () => fireEvent('ArcSDK.logout');
